import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import A from 'wanderu-component-react-router-link';
import {
    StarRating,
    Typography,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import { getRatingLabel } from '../../../../utils';
import TextReview from './TextReview/TextReview';

import styles from './IndividualReviews.scss';

const PUNCTUATION = ['.', '?', '!', '…'];
function formatReviewPart(part) {
    let cleaned = part.trim();
    if (!PUNCTUATION.includes(cleaned.charAt(cleaned.length - 1))) {
        cleaned += '.';
    }

    if (/\r|\n/.test(part)) {
        cleaned += '\n';
    }

    return cleaned;
}

function IndividualReviews(props) {
    const {
        id,
        i18nMoment,
        namespace,
        review,
        t,
        i18nNumber,
        firstSection,
        secondSection,
    } = props;
    const {
        content,
        stats,
    } = review;
    const {
        individualReviewCardTravelerType,
        individualReviewCardOverall,
    } = content;
    const {
        reviewIndividualFirstName,
        reviewIndividualDepartDate,
        reviewIndividualCons: cons,
        reviewIndividualPros: pros,
        reviewIndividualTips: tips,
        reviewCarrierGuideSlug,
        reviewCarrierName,
        c2cUrlSlug,
        departCityName: reviewDepartCity,
        arriveCityName: reviewArriveCity,
    } = stats;

    const reviewDate = i18nMoment(reviewIndividualDepartDate).format('LL');
    const ratingLabel = getRatingLabel(individualReviewCardOverall);
    const localizedRatingLabel = i18nNumber(ratingLabel)
        .format('decimal', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const ratingId = `${reviewIndividualFirstName}-${reviewDate}-${ratingLabel}`;

    let combined = pros || cons || tips;
    const reviewParts = [
        [pros, 'travelReviewCommonPros'],
        [cons, 'travelReviewCommonCons'],
        [tips, 'travelReviewCommonTips'],
    ].filter(([reviewPart]) => !!reviewPart);

    if (reviewParts.length > 1) {
        combined = reviewParts.reduce((c, [reviewPart, labelKey]) => {
            const part = formatReviewPart(reviewPart);
            return `${c} ${t(labelKey, { ns: namespace })}: ${part}`;
        }, '');
    }

    const reviewTypeSuffix = individualReviewCardTravelerType
        ? `- ${individualReviewCardTravelerType}`
        : '';

    const showCarrierSlug = !!reviewCarrierGuideSlug || !!reviewCarrierName;
    const hasCityNames = !!reviewDepartCity && !!reviewArriveCity;
    const showC2CSlug = !!c2cUrlSlug || hasCityNames;

    let href;
    let linkText;
    if (showCarrierSlug && !!reviewCarrierGuideSlug) {
        href = `/carrier/${reviewCarrierGuideSlug}`;
        linkText = reviewCarrierName;
    }
    if (showC2CSlug && !!c2cUrlSlug) {
        href = c2cUrlSlug;
        linkText = t(`${namespace}:userReviewRoute`, { reviewDepartCity, reviewArriveCity });
    }

    const wrapperClasses = classNames({
        [styles.individualReviewCard]: !firstSection,
        [styles.reviewLastCard]: firstSection,
        [styles.reviewFirstCard]: secondSection,
    });

    return (
        <div className={wrapperClasses} id={id} >
            <div className={styles.ratingContainer}>
                <StarRating
                    className={styles.starRating}
                    hoverEffects="none"
                    id={ratingId}
                    rating={ratingLabel - 1}
                    starClassNames={{
                        base: styles.stars,
                        isSelected: styles.starIsSelected,
                    }}
                />
                <Typography display="inline" id={ratingId}>
                    {localizedRatingLabel}
                </Typography>
            </div>
            <div className={styles.reviewCardLabel}>
                <Typography
                    component="span"
                    variant="h6"
                >
                    {reviewIndividualFirstName}
                </Typography>
                <Typography
                    className={styles.reviewCardType}
                    display="inline"
                >
                    {`${reviewDate}${reviewTypeSuffix}`}
                </Typography>
                {!!linkText && (
                    <>
                        {href
                            ? (
                                <A
                                    className={styles.carrierLink}
                                    href={href}
                                >
                                    {linkText}
                                </A>
                            )
                            : (
                                <Typography display="inline">
                                    {linkText}
                                </Typography>
                            )}
                    </>
                )}
            </div>
            <div className={styles.textReview}>
                {combined && (
                    <TextReview
                        review={combined}
                    />
                )}
            </div>
        </div>
    );
}

IndividualReviews.propTypes = {
    id: PropTypes.string,
    namespace: PropTypes.string.isRequired,
    review: PropTypes.object.isRequired,
};

export { IndividualReviews };
export default localize(IndividualReviews);
